/**
 * Which building a student is in, as a function of grade — parent-facing copy.
 *
 * CANONICAL COPY LIVES IN `backend/services/schoolBuilding.js`. This is a deliberate
 * duplication: Create React App will not import from outside `src/`, and a rule this small is
 * not worth an API round trip on every keystroke.
 *
 * Drift between the two is safe by design, and that is the point. This copy only PRE-FILLS the
 * select so a parent does not have to think about it; the backend re-derives the building from
 * grade when it prints the DESE application, and a recognised option always wins over
 * anything else. So if these two ever disagree, the printed form is still right — the worst
 * case is a pre-filled value the parent has to correct.
 *
 * Van Buren R-1 has two buildings, split at 7th grade, per Brandy. Preschool is offered
 * separately because that is how a preschool parent thinks of it, but Lumen SIS files it under
 * Elementary so it reports as Elementary. Junior high exists as a concept in the district but
 * not as its own building.
 */

export const BUILDING_OPTIONS = [
  { value: 'Elementary', label: 'Elementary' },
  { value: 'High School', label: 'High School' },
  { value: 'Preschool', label: 'Preschool (part of Elementary)' }
];

/** Grades describing a child who attends no building; they get no building, not a guess. */
const NOT_IN_A_BUILDING = ['not in school', 'daycare', 'n/a', 'none'];

/**
 * Building implied by a grade, or '' when the child attends no building, or null when the
 * grade cannot be read. Mirrors buildingForGrade in the backend service.
 */
export function buildingForGrade(grade) {
  const g = String(grade == null ? '' : grade).trim().toLowerCase();
  if (!g) return null;
  if (NOT_IN_A_BUILDING.includes(g)) return '';
  if (/^(pre-?k|pre-?school|pre school|prek)/.test(g)) return 'Preschool';
  if (/^(k|kindergarten|kg)$/.test(g)) return 'Elementary';
  const digits = g.match(/\d{1,2}/);
  if (!digits) return null;
  const n = Number(digits[0]);
  if (!Number.isFinite(n) || n < 1 || n > 12) return null;
  return n >= 7 ? 'High School' : 'Elementary';
}

/**
 * Should a grade change overwrite the building already showing?
 *
 * Yes when it is empty, and yes when it still matches what the OLD grade implied - that means
 * nobody has touched it and it is stale. No when the parent has deliberately chosen something
 * else, which is the case worth protecting: a child placed outside the building their grade
 * implies. This needs no extra state, so nothing transient leaks into the submitted data.
 */
export function shouldUpdateBuildingOnGradeChange(currentBuilding, previousGrade) {
  const current = String(currentBuilding == null ? '' : currentBuilding).trim();
  if (!current) return true;
  const impliedByOld = buildingForGrade(previousGrade);
  return impliedByOld !== null && current === impliedByOld;
}
